import React, { useState } from 'react';
import type { EvaluationDetails } from '../../../../types/course';
import ConfirmDialog from '../../../common/ConfirmDialog';
import DashedPanel from './shared/DashedPanel';
import { GearIcon } from './shared/SolarIcon';

interface QuizManagementPanelProps {
  details: EvaluationDetails;
  onChange: (patch: Partial<EvaluationDetails>) => void;
  /** Fresh lookup for a DIFFERENT quiz already holding the main-quiz flag;
   *  a hit opens the "make this the main quiz instead?" confirmation. */
  onCheckMainQuizConflict?: () => Promise<{ id: string; title: string } | null>;
}

const ATTEMPT_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: null, label: 'Unlimited' },
];

/**
 * QuizManagementPanel — Figma frame "Quiz Management" (node 11126:524931).
 * Houses passing grade, max attempts dropdown, and the shuffle toggle.
 */
export default function QuizManagementPanel({
  details,
  onChange,
  onCheckMainQuizConflict,
}: QuizManagementPanelProps) {
  const { passing_grade_percent, max_attempts, shuffle_items, is_main_quiz } = details;
  const passingEnabled = passing_grade_percent !== null;
  const [mainConflict, setMainConflict] = useState<{ id: string; title: string } | null>(null);
  const [checkingMain, setCheckingMain] = useState(false);

  const handleMainQuizToggle = async (checked: boolean) => {
    if (!checked || !onCheckMainQuizConflict) {
      onChange({ is_main_quiz: checked });
      return;
    }
    setCheckingMain(true);
    try {
      const conflict = await onCheckMainQuizConflict();
      if (conflict) {
        setMainConflict(conflict);
      } else {
        onChange({ is_main_quiz: true });
      }
    } finally {
      setCheckingMain(false);
    }
  };

  return (
    <DashedPanel>
      <div className="flex items-center gap-3 mb-5">
        <GearIcon size={22} className="text-primary-500" />
        <h3 className="m-0 font-sans font-bold text-lg leading-7 text-text-primary">
          Quiz Management
        </h3>
      </div>

      <div className="flex flex-col gap-6">
        {/* Passing grade */}
        <div>
          <p className="mb-2.5 font-sans font-medium text-sm leading-5 text-text-primary">
            Is there a passing grade? If so, enter it as a percentage.
          </p>
          <input
            type="number"
            min={0}
            max={100}
            value={passing_grade_percent ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return onChange({ passing_grade_percent: null });
              onChange({ passing_grade_percent: Math.max(0, Math.min(100, Number(raw))) });
            }}
            placeholder="Write in %…"
            className="w-full h-[46px] px-[18px] rounded-[50px] border border-text-secondary bg-transparent text-text-primary font-sans text-sm outline-none focus:border-primary-500 transition-colors"
          />
          <button
            type="button"
            onClick={() => onChange({ passing_grade_percent: passingEnabled ? null : 70 })}
            className="w-full h-11 mt-3 px-3.5 rounded-[10px] bg-primary-500 border-0 font-sans font-bold text-[13px] leading-6 cursor-pointer transition-all hover:brightness-95"
          >
            {passingEnabled
              ? 'Or click here to remove a passing grade'
              : 'Click here to set a passing grade (70%)'}
          </button>
        </div>

        {/* Max attempts — only meaningful when failing is possible, i.e. a
            passing grade is set. Without one there is nothing to retry. */}
        {passingEnabled && (
        <div>
          <p className="mb-2.5 font-sans font-medium text-sm leading-5 text-text-primary">
            How many attempts are permitted in case of failing?
          </p>
          <div className="flex flex-wrap gap-2">
            {ATTEMPT_OPTIONS.map((opt) => {
              const selected = opt.value === max_attempts;
              const chipClass = selected
                ? 'border-primary-500 bg-primary-500/10 text-primary-500 font-bold'
                : 'border-text-secondary bg-transparent text-text-primary font-medium';
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => onChange({ max_attempts: opt.value })}
                  aria-pressed={selected}
                  className={`min-w-14 h-10 px-4 rounded-[50px] border font-sans text-[13px] transition-all duration-150 ease-out cursor-pointer ${chipClass}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Shuffle */}
        <label className="flex items-center gap-3 self-start font-sans text-sm text-text-primary cursor-pointer">
          <input
            type="checkbox"
            checked={shuffle_items}
            onChange={(e) => onChange({ shuffle_items: e.target.checked })}
            className="w-[18px] h-[18px] accent-primary-500"
          />
          Shuffle questions for each learner
        </label>

        {/* Main quiz */}
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-3 self-start font-sans text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={!!is_main_quiz}
              disabled={checkingMain}
              onChange={(e) => void handleMainQuizToggle(e.target.checked)}
              className="w-[18px] h-[18px] accent-primary-500 disabled:cursor-wait"
              data-testid="main-quiz-toggle"
            />
            Select as the main quiz
          </label>
          <div
            role="note"
            className="rounded-2xl px-3.5 py-3 bg-secondary-500/10 border border-secondary-500/40 font-sans text-[13px] leading-5 text-text-primary"
          >
            Creating just one quiz? It&rsquo;s automatically the main quiz — no need to
            touch this toggle. With several quizzes, turn it on for the one that should
            count: the main quiz powers your analytics and the daily Top Learners
            ranking. If none is marked as main, the average of all quizzes is used
            instead. The CSV export always includes every quiz in detail.
          </div>
        </div>
      </div>

      {mainConflict && (
        <ConfirmDialog
          title="Change the Main Quiz?"
          message={`"${mainConflict.title}" is currently set as the main quiz. Make this quiz the main one instead?`}
          confirmLabel="Make this the main quiz"
          cancelLabel="Cancel"
          onConfirm={() => {
            setMainConflict(null);
            onChange({ is_main_quiz: true });
          }}
          onCancel={() => setMainConflict(null)}
        />
      )}
    </DashedPanel>
  );
}
